import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useGetVendorsMutation,
  useGetLookupValuesMutation,
  useUpdateVendorStatusMutation,
  Lookup,
} from "../api/api";
import { useToast } from "../@/components/ui/use-toast";
import Loader from "../components/LoaderComponent";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "../@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../@/components/ui/table";
import { MoveUp, MoveDown, ArrowUpDown } from "lucide-react";
import useDebounce, { formatDate, formatDateTime } from "../lib/utils";
import SkeletonTableLoading from "../skeletonLoading/SkeletonTableLoading";
import { DateRange } from "react-day-picker";
import { DatePickerWithRange } from "../components/DateRangePicker";
import { LOOKUPS } from "../lib/constants";
import Pagination from "../components/Pagination";
import { FiSearch } from "react-icons/fi";
import { MdModeEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import SingleDatePicker from "../components/SingleDatePicker";
import ServiceTypeDropdown from "./AdminConfigurationPage/ServiceTypeDropDown";

declare module "@tanstack/table-core" {
  interface ColumnMeta<TData extends unknown, TValue> {
    isTooltip?: boolean;
  }
}

interface Vendors {
  id: number;
  company: string;
  statusId: number;
  createdAt: string;
  statusName: string;
  phone: string;
  user: string;
  vendorImage: string;
  serviceTypeId: number;
  serviceTypeName: string;
}

const VendorDetails: React.FC = () => {
  const [getVendors, { isLoading: vendorsLoading }] = useGetVendorsMutation();
  const [getLookupValues, { isLoading: statusesLoading }] =
    useGetLookupValuesMutation();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize, _] = useState<number>(10);
  const [selected, setSelected] = useState<any>({
    name: "All Vendors",
    id: undefined,
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchQuery = useDebounce(searchTerm, 500);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [updateVendorStatus, { isLoading: updatingVendors }] =
    useUpdateVendorStatusMutation();
  const [vendors, setVendors] = useState<Vendors[]>([]);
  const [statuses, setStatuses] = useState<Lookup[]>([]);
  const [sortingOrder, setSortingOrder] = useState<"asc" | "desc" | undefined>(
    "desc"
  );
  const [sortingColumn, setSortingColumn] = useState<string | undefined>(
    "createdAt"
  );
  const options = [
    { name: "All Vendors", id: undefined },
    { name: "Activated", id: 1 },
    { name: "Inactivated", id: 2 },
    { name: "Suspended", id: 3 },
  ];
  const { toast } = useToast();
  const [dateRangeValue, setDateRangeValue] = useState<DateRange | undefined>(
    undefined
  );
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [selectedServiceType, setSelectedServiceType] = useState<string>("");

  const navigate = useNavigate();

  const fetchStatuses = async () => {
    const result = await getLookupValues({
      lookupIdDto: { lookupId: LOOKUPS.VendorLookup },
    });
    if (result.data) {
      setStatuses(result.data.data);
    }
  };

  const fetchVendors = useCallback(async () => {
    const serviceTypeIdValue = selectedServiceType
      ? parseInt(selectedServiceType)
      : undefined;
    const result: any = await getVendors({
      getVendorRequestDto: {
        searchQuery:
          debouncedSearchQuery !== "" ? debouncedSearchQuery : undefined,
        pageNumber,
        pageSize,
        getTotalCount: true,
        sortingColumn,
        sortingOrder,
        statusId: selected.id,
        startDate: startDate
          ? formatDateTime(startDate, "YYYY-MM-DDTHH:mm:ssZ")
          : undefined,
        endDate: endDate
          ? formatDateTime(endDate, "YYYY-MM-DDTHH:mm:ssZ")
          : undefined,
        ...(serviceTypeIdValue && {
          serviceTypeId:
            serviceTypeIdValue === 0 ? undefined : serviceTypeIdValue,
        }),
      },
    }).unwrap();
    setVendors(result?.data?.vendors);
    setTotalCount(result?.data?.totalCount || 0);
  }, [
    getVendors,
    debouncedSearchQuery,
    selected.id,
    pageNumber,
    sortingColumn,
    sortingOrder,
    startDate,
    endDate,
    selectedServiceType,
  ]);

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchStatuses();
        await fetchVendors();
      } catch (err: any) {
        console.log("Err: ", err?.data?.message);
        toast({
          title: "Error!",
          description: `${err?.data?.message}`,
        });
      }
    };

    loadData();
  }, [
    debouncedSearchQuery,
    selected.id,
    pageNumber,
    sortingColumn,
    sortingOrder,
    startDate,
    endDate,
    selectedServiceType,
  ]);

  const handleStatusChange = async (vendorId: number, lookupId: number) => {
    try {
      const response = await updateVendorStatus({
        vendorStatusDto: {
          vendorId,
          lookupId,
        },
      });

      if (response.data) {
        await fetchVendors();

        toast({
          title: "Success!",
          description: `${response?.data?.message}`,
        });
      }
    } catch (error) {
      console.log("Error", error);
    }
  };

  const handlePageChange = (newPageNumber: number) => {
    if (newPageNumber < 0 || newPageNumber > Math.ceil(totalCount / pageSize)) {
      return;
    }
    setPageNumber(newPageNumber);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (pageNumber !== 1) {
      setPageNumber(1);
    }
    setSearchTerm(event.target.value);
  };

  const handleSorting = (column: string) => {
    if (sortingColumn === column) {
      setSortingOrder(sortingOrder === "asc" ? "desc" : "asc");
    } else {
      setSortingColumn(column);
      setSortingOrder("asc");
    }
    setPageNumber(1);
  };

  const columns = useMemo<ColumnDef<Vendors>[]>(
    () => [
      {
        accessorKey: "company",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("company")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Company{" "}
            {sortingColumn === "company" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
        cell: ({ row }) => (
          <div className="flex items-center">
            <img
              src={row.original.vendorImage}
              alt={row.original.company}
              className="w-8 h-8 rounded-full mr-2"
              onError={(e) => {
                e.currentTarget.src = "/storeDummyImg.png";
              }}
            />
            <span className="overflow-hidden text-ellipsis text-nowrap">
              {row.original.company}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "user",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("user")}
            className="cursor-pointer flex gap-1 items-center"
          >
            User Name{" "}
            {sortingColumn === "user" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
      },
      {
        accessorKey: "serviceTypeName",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("serviceTypeName")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Service Type{" "}
            {sortingColumn === "serviceTypeName" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
      },
      {
        accessorKey: "email",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("email")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Email{" "}
            {sortingColumn === "email" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 120,
      },
      {
        accessorKey: "phone",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("phone")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Phone{" "}
            {sortingColumn === "phone" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
      },
      {
        accessorKey: "statusId",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("statusId")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Status{" "}
            {sortingColumn === "statusId" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 150,
        cell: ({ row }) => (
          <select
            className="border border-gray-300 rounded-md py-1 px-2"
            value={row.original.statusId}
            onChange={(e) =>
              handleStatusChange(row.original.id, Number(e.target.value))
            }
          >
            {statuses.map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
        ),
        meta: {
          isTooltip: false,
        },
      },
      {
        accessorKey: "createdAt",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("createdAt")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Added On
            {sortingColumn === "createdAt" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
        cell: ({ cell }) => (
          <span>{formatDate(cell.getValue() as string, "dd MMM yyyy")}</span>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <div className="flex space-x-2 justify-center">
            <button
              onClick={() => {
                let serviceType = row.original.serviceTypeId
                  ? "&serviceTypeId=" + row.original.serviceTypeId
                  : undefined;

                navigate(
                  `/store?vendorId=${row.original.id}${
                    serviceType !== undefined ? serviceType : ""
                  }`
                );
              }}
            >
              <MdModeEdit className="text-[#72767E] hover:text-black" />
            </button>
          </div>
        ),
        size: 80,
        meta: {
          isTooltip: false,
        },
      },
    ],
    [sortingColumn, sortingOrder, statuses]
  );

  const table = useReactTable({
    data: vendors,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: Math.ceil(totalCount / pageSize),
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageNumber - 1,
        pageSize,
      },
    },
  });

  return (
    <div className="pl-6 pr-6 pt-4 bg-grayBackgroundColor w-full overflow-hidden">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mt-1 md:mt-0">
        <div className="flex justify-between bg-white rounded-lg border p-[1px] border-gray-300 w-full md:w-auto">
          {options.map((option) => (
            <button
              key={option.name}
              onClick={() => {
                setPageNumber(1);
                setSelected(option);
              }}
              className={`flex-1 min-w-20 p-2 text-center text-[10px] md:text-xs ${
                selected.name === option.name
                  ? "bg-customGreenLight text-customGreenColor font-base"
                  : "text-gray-500 hover:bg-gray-100"
              } rounded-lg ${
                option.name === "All Vendors" ? "rounded-l-lg" : ""
              } ${option.name === "Draft" ? "rounded-r-lg" : ""}`}
            >
              {option.name}
            </button>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full md:w-auto">
          <div className="w-full sm:w-auto">
            <ServiceTypeDropdown
              selectedServiceType={selectedServiceType}
              onChange={(value) => {
                setSelectedServiceType(value);
                setPageNumber(1);
              }}
            />
          </div>

          <div className="relative flex justify-center text-customGrayText items-center w-full sm:w-48">
            <FiSearch className="absolute left-2 text-lg" />
            <input
              type="search"
              className="text-sm p-4 pl-8 h-9 w-full rounded border-[1px] bg-white searchInput border-gray-300 outline-none"
              placeholder="Search vendors"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <div className="w-full sm:w-auto">
            <SingleDatePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateSelect={setStartDate}
              onEndDateSelect={setEndDate}
            />
          </div>
        </div>
      </div>

      {vendorsLoading || statusesLoading ? (
        <>
          <div className="overflow-y-auto h-[calc(100vh-12rem)] bg-white rounded-lg shadow mt-4">
            <div className="h-full">
              <Table>
                <TableHeader className="bg-customGreenColor">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className="text-white"
                          style={{
                            minWidth: header.column.columnDef.size,
                            maxWidth: header.column.columnDef.size,
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
              </Table>
              <SkeletonTableLoading />
            </div>
          </div>
        </>
      ) : (
        <div className="overflow-y-auto h-[calc(100vh-12rem)] bg-white rounded-lg shadow mt-4">
          <div className="h-full">
            <Table>
              <TableHeader className="bg-customGreenColor sticky top-0">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className="text-white"
                        style={{
                          minWidth: header.column.columnDef.size,
                          maxWidth: header.column.columnDef.size,
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          className="overflow-hidden text-ellipsis"
                          key={cell.id}
                          style={{
                            minWidth: cell.column.columnDef.size,
                            maxWidth: cell.column.columnDef.size,
                          }}
                          isTooltip={
                            cell.column.columnDef.meta?.isTooltip ?? true
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="text-center">
                      No vendors found for the selected service type.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {vendors.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 pb-4 overflow-hidden">
          <div className="text-[#667085] text-sm">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {Math.ceil(totalCount / pageSize)}
          </div>
          <Pagination
            totalCount={totalCount}
            pageSize={pageSize}
            currentPage={pageNumber}
            onPageChange={handlePageChange}
          />
        </div>
      )}
      {updatingVendors && <Loader loading={"Updating..."} />}
    </div>
  );
};
export default VendorDetails;
